import React from "react";
import { Container } from "@/components/Shared";
import { trackOutboundLink } from "@/lib/analytics";
import { JORGE } from "@/constants";

interface KickBannerProps {
  language?: "en" | "es";
}

const KICK_URL = JORGE.SOCIAL.KICK;

const TOPICS = [
  { en: "Local Business Growth", es: "Crecimiento Local", color: "#22c55e" },
  { en: "Digital Marketing", es: "Marketing Digital", color: "#f59e0b" },
  { en: "SEO That Works", es: "SEO Que Funciona", color: "#3b82f6" },
  { en: "AI for Business", es: "IA para Negocios", color: "#a855f7" },
] as const;

const content = {
  en: {
    badge: "On Kick",
    headline: "See How I Grow Local Businesses",
    description:
      "Watch real client projects come to life — from website builds to SEO strategies that drive foot traffic. Learn what actually works for small business marketing in 2026, straight from the operator doing the work.",
    cta: "Follow on Kick",
    secondary: "See Live Streams",
    secondaryLink: "/live",
    tagline: "kick.com/marketbrewer",
  },
  es: {
    badge: "En Kick",
    headline: "Mira Cómo Hago Crecer Negocios Locales",
    description:
      "Mira proyectos reales de clientes cobrar vida — desde sitios web hasta estrategias SEO que atraen clientes. Aprende lo que realmente funciona para el marketing de pequeños negocios en 2026.",
    cta: "Seguir en Kick",
    secondary: "Ver En Vivo",
    secondaryLink: "/es/en-vivo",
    tagline: "kick.com/marketbrewer",
  },
};

const KickBanner: React.FC<KickBannerProps> = ({ language = "en" }) => {
  const t = content[language];

  // One click, one event. This used to also fire cta_click, so every Kick follow was
  // double-counted in total event volume.
  const handleKickClick = () => {
    trackOutboundLink(KICK_URL);
  };

  return (
    <section className="relative overflow-hidden bg-dark-950">
      {/* Kick green accent line at top */}
      <div className="h-px bg-gradient-to-r from-transparent via-[#53FC18]/40 to-transparent" />


      <Container size="xl" className="relative z-10 py-14 md:py-20">
        <div className="grid lg:grid-cols-[1fr_auto] gap-10 items-center">
          {/* Left: Content */}
          <div>
            {/* Badge */}
            <div
              className="flex items-center gap-2.5 mb-5"
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#53FC18] opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#53FC18]" />
              </span>
              <span className="text-xs font-semibold tracking-widest uppercase text-[#53FC18]">
                {t.badge}
              </span>
            </div>

            {/* Headline */}
            <h2
              className="text-3xl md:text-4xl font-bold text-dark-50 tracking-tight mb-4"
            >
              {t.headline}
            </h2>

            {/* Topic pills */}
            <div
              className="flex flex-wrap gap-2 mb-5"
            >
              {TOPICS.map((topic) => (
                <span
                  key={topic.en}
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border"
                  style={{
                    color: topic.color,
                    borderColor: `${topic.color}30`,
                    backgroundColor: `${topic.color}08`,
                  }}
                >
                  {topic[language]}
                </span>
              ))}
            </div>

            {/* Description */}
            <p
              className="text-dark-400 leading-relaxed max-w-2xl mb-7"
            >
              {t.description}
            </p>

            {/* CTAs */}
            <div
              className="flex flex-wrap items-center gap-4"
            >
              <a
                href={KICK_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleKickClick}
                data-mb-tracked="1"
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-lg transition-colors shadow-lg text-dark-950 bg-[#53FC18] hover:bg-[#47e015] active:bg-[#3cc712] shadow-[#53FC18]/20 focus:outline-none focus:ring-2 focus:ring-[#53FC18] focus:ring-offset-2 focus:ring-offset-dark-950"
              >
                <KickIcon className="w-4 h-4" />
                {t.cta}
              </a>
              <a
                href={language === "es" ? "/es/en-vivo" : "/live"}
                className="inline-flex items-center gap-1.5 px-5 py-3 text-sm font-semibold text-dark-300 rounded-lg border border-dark-700 hover:border-dark-500 hover:text-dark-50 transition-colors focus:outline-none focus:ring-2 focus:ring-metro-orange-500 focus:ring-offset-2 focus:ring-offset-dark-950"
              >
                {t.secondary}
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                  />
                </svg>
              </a>
            </div>
          </div>

          {/* Right: Kick channel card */}
          <a
            href={KICK_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleKickClick}
            data-mb-tracked="1"
            className="group hidden lg:flex flex-col items-center justify-center w-56 h-56 rounded-2xl bg-dark-800/50 border border-dark-700 hover:border-[#53FC18]/30 transition-all"
            aria-label={`Visit ${t.tagline}`}
          >
            <div className="flex items-center justify-center w-16 h-16 rounded-xl bg-[#53FC18]/10 text-[#53FC18] mb-4 group-hover:scale-110 transition-transform">
              <KickIcon className="w-8 h-8" />
            </div>
            <span className="text-sm font-mono text-[#53FC18]">{t.tagline}</span>
            <span className="text-xs text-dark-400 mt-1">
              {language === "es" ? "Transmisiones en Kick" : "Streams on Kick"}
            </span>
          </a>
        </div>
      </Container>

      {/* Bottom accent */}
      <div className="h-px bg-gradient-to-r from-transparent via-dark-700 to-transparent" />
    </section>
  );
};

function KickIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M1.333 0h8v5.333H12V2.667h2.667V0h8v8H20v2.667h-2.667v2.666H20V16h2.667v8h-8v-2.667H12v-2.666H9.333V24h-8Z" />
    </svg>
  );
}

export default KickBanner;
